import React from 'react'
import classNames from 'classnames'
import {useWindowSize} from 'react-use'
import axios from 'axios'
import {strftime} from '@fotolog/xutil'
import {useSuspense, useController} from '@data-client/react'
import {TripResource, FotoResource, Trip} from '../lib/model'
import {apiReply, FormFieldProps, galleryCaption, SelectOption, toast, useAppNav, useForm, useSession, val2opt} from '../lib'
import {AppMsg, Field, PanelProps, SelectControl, SelectTags, SubmitButton} from '.'
import {Foto} from '../lib/model'
import {UseFormReturn} from 'react-hook-form'
import {UpdateFotoSchema} from '../lib/schema/Foto'
import {UpdateFotoForm} from '../lib/form/Foto'
import {useMapState} from './Map'
import {micromark} from 'micromark'
import {Box, Button, Card, CardActions, CardContent, CardHeader, CardMedia, IconButton, Typography} from '@mui/material'
import {IconDelete, IconUpdate, IconLocate, IconShare, IconTripMap} from '../Icons'
import {IMapable} from '@fotolog/data'

type FotoSummaryProps = {
  foto: Foto
  size?: number
  truncate?: boolean
}
function FotoSummary({foto, size = 100, truncate = true}: FotoSummaryProps) {
  return (
    <Box display="flex" alignItems="center">
      <Box
        mr={1}
        className="app-profile-img"
        sx={{
          width: size,
          height: size,
          borderRadius: '10%',
          backgroundImage: `url(${foto.thumbnail})`,
        }}
      />
      <div className={truncate ? 'app-truncate' : ''} title={foto.caption}>
        {foto.caption || foto.took ? strftime(foto.took, 'MMMM dd, yyyy') : '???'}
      </div>
    </Box>
  )
}

type SelectFotoProps = {
  form: UseFormReturn
  attrs: FormFieldProps
  fotos: Foto[]
  defval?: number
}
export function SelectFoto({form, attrs, fotos, defval}: SelectFotoProps) {
  // TODO: only fotos with gps defined?
  const name = attrs.name
  const option = (foto: Foto) => ({
    value: foto.id,
    label: <FotoSummary foto={foto} size={50} />,
  })
  React.useEffect(() => {
    if (fotos.length === 1) {
      form.setValue(name, option(fotos[0]), {shouldValidate: true})
    } else if (defval) {
      const item = fotos.find(i => i.id === defval)
      if (item) {
        form.setValue(name, option(item), {shouldValidate: true})
      }
    }
  }, [defval, fotos.map(i => i.id).join(',')]) // eslint-disable-line react-hooks/exhaustive-deps
  const options = fotos.map(option) as SelectOption[]
  return <SelectControl form={form} attrs={{...attrs, options}} />
}

type FotoCardProps = {
  trip: Trip
  foto: Foto
  isPublic: boolean
  isSubTrip?: boolean
}

export function FotoCard({trip, foto, isPublic, isSubTrip = false}: FotoCardProps) {
  const nav = useAppNav()
  const {width} = useWindowSize()
  const map = useMapState()
  const mapable =
    foto.gps &&
    ({
      foto,
      id: foto.id,
      title: foto.caption,
      img: foto.thumbnail,
      gps: foto.gps,
      marker: false,
      clicked: 0,
    } as IMapable)

  return (
    <Card>
      <CardHeader
        action={
          mapable && (
            <IconLocate
              onClick={() => {
                map.openMarker(mapable)
                if (width < 500) {
                  nav.back()
                }
              }}
              className="app-clickable"
              color={map.marker?.id === foto.id ? 'secondary' : 'primary'}
              fontSize="large"
            />
          )
        }
        title={foto.caption}
      />
      <CardMedia
        component="img"
        width="100%"
        image={foto.thumbnail}
        alt={foto.caption || `Foto ${foto.id}`}
        onClick={() => trip.lightbox(foto)}
        className="app-clickable app-framed"
      />
      {foto.story && (
        <CardContent>
          <Typography dangerouslySetInnerHTML={{__html: micromark(foto.story)}} />
        </CardContent>
      )}
      <CardActions disableSpacing>
        {isSubTrip && (
          <IconButton href={`/trip/${foto.tripId}`} color="primary" aria-label="view trip" title="View Trip">
            <IconTripMap/>
          </IconButton>
        )}
        {!isPublic && !isSubTrip && (
          <IconButton href={`../${foto.id}/update`} color="primary" aria-label="update" title="Update Foto">
            <IconUpdate />
          </IconButton>
        )}
        {!isPublic && !isSubTrip && (
          <IconButton href={`../${foto.id}/delete`} color="primary" aria-label="delete" title="Delete Foto">
            <IconDelete />
          </IconButton>
        )}
        <IconButton
          color="primary"
          aria-label="share"
          title="Share Foto"
          onClick={() => {
            // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/share
            // https://developer.mozilla.org/en-US/docs/Web/Manifest/share_target
            const url = `https://fotolog.tfks.net/trip/${trip.id}-${trip.slug}/${foto.id}`
            if (navigator.share) {
              navigator.share({
                url,
                title: `${trip.title} : ${foto.caption}`,
                text: foto.story,
              })
            } else {
              navigator.clipboard.writeText(url)
              toast('info', 'Link Copied to Clipboard')
            }
          }}
        >
          <IconShare />
        </IconButton>
        <Box textAlign="right" fontSize={14} color="text.secondary" flexGrow={1} whiteSpace="pre">
          {foto.took ? strftime(foto.took, "PPP'\n'pp") : 'NO TIME'}
        </Box>
      </CardActions>
    </Card>
  )
}

export function UpdateFoto({session, params}: PanelProps) {
  const nav = useAppNav()
  const api = useController()
  const foto = useSuspense(FotoResource.get, {id: params['foto']})
  const defaults = {...foto, tags: foto.tags?.map(val2opt), location: ''}
  if (foto.gps) {
    defaults.location = `${foto.gps.lat}, ${foto.gps.lng}`
  }
  const form = useForm(UpdateFotoSchema, {defaults})

  /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
  /* @ts-expect-error */
  const onSubmit = form.handleSubmit(async (data: UpdateFotoSchema) => {
    const msg = toast('loading', 'Updating Foto')
    let tags: string[]
    if (data.tags) {
      tags = data.tags.map(i => i.value)
    }
    const reply = await apiReply(() => api.fetch(FotoResource.update, {id: foto.id}, {...data, tags}))
    if (reply.error) {
      msg.fail(reply.error)
      return
    }
    msg.succeed()
    api.invalidate(TripResource.get, {id: foto.tripId})
    nav.back()
  })

  return (
    <>
      <img className="app-framed" src={foto.url} width="100%" alt="foto" />
      <form onSubmit={onSubmit}>
        <Box my={5}>
          <Field form={form} attrs={UpdateFotoForm.caption} />
        </Box>
        <Box my={5}>
          <Field form={form} attrs={UpdateFotoForm.rating} />
        </Box>
        <Box my={5}>
          <Field form={form} attrs={UpdateFotoForm.took} />
        </Box>
        <Box my={5}>
          <Field form={form} attrs={UpdateFotoForm.story} />
        </Box>
        <Box my={5}>
          <Field form={form} attrs={UpdateFotoForm.location} />
        </Box>
        <Box my={5}>
          <SelectTags form={form} attrs={UpdateFotoForm.tags} tags={session.scope.tags} />
        </Box>
        <Box mt={3} textAlign="center">
          <SubmitButton form={form}>Save Changes</SubmitButton>
        </Box>
      </form>
    </>
  )
}

type FotoRowProps = {
  foto: Foto
}
function FotoRow({foto}: FotoRowProps) {
  return (
    <tr data-src={foto.url} data-id={foto.id}>
      <td className="text-center">
        <img
          className={classNames(`lg-trip-${foto.tripId}`, 'app-col', 'foto', 'small', 'app-clickable')}
          src={foto.thumbnail}
          data-src={foto.url}
          data-thumb={foto.thumbnail}
          data-download={foto.caption.replace(/\W+/ug, '-') + '.jpg'}
          data-download-url={foto.urls?.orig}
          data-sub-html={galleryCaption(foto.caption, foto.story)}
          alt={foto.caption}
          title={foto.caption}
        />
      </td>
      <td className="app-truncate hide-on-mobile">{foto.caption}</td>
      <td className="text-left">{strftime(foto.took, 'MMM dd, yyyy')}</td>
    </tr>
  )
}

type FotosTableProps = {
  items: Foto[]
}
export function FotosTable({items}: FotosTableProps) {
  return (
    <table className="hover" border={0}>
      <thead>
        <tr>
          <th className="text-center" style={{width: 100}}>
            Thumbnail
          </th>
          <th className="text-left hide-on-mobile">Caption</th>
          <th className="text-left" style={{width: 120}}>
            Date
          </th>
        </tr>
      </thead>
      <tbody>
        {items.map(i => (
          <FotoRow key={i.id} foto={i} />
        ))}
      </tbody>
    </table>
  )
}

type FotoUploaderProps = {
  trip: Trip
  children: React.ReactNode
}
export function FotoUploader({trip, children}: FotoUploaderProps) {
  const session = useSession()
  const api = useController()

  const onFilesSelected = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    const msg = toast('loading', 'Uploading Fotos')
    const files = evt.target.files
    if (files == null) {
      return
    }
    const form = new FormData()
    for (const i in files) {
      form.append('image', files[i])
    }
    await axios.post(`/api/trip/${trip.id}/fotos`, form, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'App-User': `${session.id}`, // helps with trouble-shooting
      },
      onUploadProgress: evt => {
        if (evt.progress) {
          msg.progress(Math.floor(100 * evt.progress))
        }
      },
    })
    msg.succeed()
    api.invalidate(TripResource.get, {id: trip.id})
  }

  return (
    <Button component="label" color="primary" title="Upload Fotos">
      {children}
      <input
        id="files"
        type="file"
        accept=".jpg,.jpeg,.heic"
        capture="environment"
        onChange={onFilesSelected}
        multiple
        hidden
      />
    </Button>
  )
}

// interface FotoDeleterProps {
//   foto?: Foto
//   onClose: () => void
// }

// export function FotoDeleter({foto, onClose}: FotoDeleterProps) {
//   const isOpen = Boolean(foto)
//   const api = useController()

//   const onDelete = async () => {
//     if (!foto) return // mostly to satisfy compiler not-null checks
//     const msg = toast('loading', 'Deleting Foto')
//     const reply = await apiReply(() => api.fetch(FotoResource.delete, {id: foto.id}))
//     if (reply.error) {
//       msg.fail(reply.error)
//       return
//     }
//     msg.succeed()
//     api.invalidate(TripResource.get, {id: foto.tripId})
//     onClose()
//   }

//   // NOTE: consider this if do a lot of confirmation dialogs:
//   // - https://www.npmjs.com/package/material-ui-confirm
//   return (
//     <Dialog open={isOpen} onClose={onClose} aria-labelledby="dialog-title">
//       <DialogTitle id="dialog-title" sx={{p: 0, mb: 2}}>
//         <Alert severity="error" sx={{fontSize: '1.1rem'}}>
//           Delete Confirmation
//         </Alert>
//       </DialogTitle>
//       <DialogContent>
//         <DialogContentText>
//           Deleting a Foto cannot be undone.
//           <br />
//           Are you sure?
//         </DialogContentText>
//       </DialogContent>
//       <DialogActions>
//         <Button onClick={onClose} autoFocus>
//           Cancel
//         </Button>
//         <Button onClick={onDelete}>Yes, Delete</Button>
//       </DialogActions>
//     </Dialog>
//   )
// }

export function DeleteFoto({session, params}: PanelProps) {
  const nav = useAppNav()
  const api = useController()
  const foto = useSuspense(FotoResource.get, {id: params['foto']})

  const onSubmit = async () => {
    const msg = toast('loading', 'Deleting Foto')
    const reply = await apiReply(() => api.fetch(FotoResource.delete, {id: foto.id}))
    if (reply.error) {
      msg.fail(reply.error)
      return
    }
    msg.succeed()
    api.invalidate(TripResource.get, {id: foto.tripId})
    nav.back()
  }

  return (
    <>
      <Typography variant="h3" my={3}>
        {foto.caption}
      </Typography>
      <AppMsg severity="error" size="medium">
        This Action Cannot Be Undone.
        <br />
        Make sure to have a copy
        <br />
        if this image is important.
        <br />
        <br />
        Are you sure?
      </AppMsg>
      <Box mt={3} display="flex" justifyContent="center">
        <Button color="secondary" sx={{mr: 2}} onClick={() => nav.back()}>
          Cancel
        </Button>
        <Button color="primary" onClick={onSubmit}>
          Yes, Delete
        </Button>
      </Box>
    </>
  )
}
